import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Mail, MapPin, Clock } from "lucide-react";
import { FullLogo } from "./Logo";
import { useT } from "@/lib/i18n";
import { FOOTER } from "../constants/testIds";

const NAV_LINKS = [
  { to: "/", key: "home" },
  { to: "/about", key: "about" },
  { to: "/markets", key: "markets" },
  { to: "/partners", key: "partners" },
  { to: "/hubs", key: "hubs" },
  { to: "/contact", key: "contact" },
];

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn", href: "#" },
  { icon: Twitter, label: "X / Twitter", href: "#" },
  { icon: Mail, label: "Email", href: "mailto:info@nexxishub.com" },
];

export const Footer = () => {
  const t = useT();
  return (
    <footer
      data-testid={FOOTER.root}
      className="relative overflow-hidden paper-grain"
      style={{ background: "var(--nexxis-bg)", color: "var(--nexxis-ink)" }}
    >
      {/* soft fade so the previous section melts into the footer (no hard line) */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-40"
        aria-hidden="true"
        style={{ background: "linear-gradient(to bottom, var(--nexxis-bg-alt), transparent)" }}
      />
      {/* thin coral accent rule that fades out across the top */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        aria-hidden="true"
        style={{ background: "linear-gradient(to right, transparent, rgba(255,104,87,0.55) 35%, rgba(255,104,87,0.55) 65%, transparent)" }}
      />

      {/* soft brand glow (with a stronger coral presence) */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-24 left-[22%] h-72 w-72 rounded-full blur-3xl" style={{ background: "rgba(13,95,94,0.18)" }} />
        <div className="absolute -bottom-28 right-[18%] h-80 w-80 rounded-full blur-3xl" style={{ background: "rgba(77,182,172,0.16)" }} />
        <div className="absolute bottom-[-10%] left-[6%] h-72 w-72 rounded-full blur-3xl" style={{ background: "rgba(255,104,87,0.20)" }} />
        <div className="absolute top-[28%] right-[30%] h-52 w-52 rounded-full blur-3xl" style={{ background: "rgba(255,104,87,0.12)" }} />
      </div>

      <div className="relative mx-auto max-w-[1200px] px-5 sm:px-8 py-16 sm:py-20">
        {/* glass panel */}
        <div
          className="rounded-[20px] p-8 sm:p-10 lg:p-12"
          style={{
            background: "var(--nexxis-glass)",
            border: "1px solid var(--nexxis-glass-border)",
            backdropFilter: "blur(12px) saturate(140%)",
            WebkitBackdropFilter: "blur(12px) saturate(140%)",
            boxShadow: "var(--nexxis-shadow-glass)",
          }}
        >
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            {/* brand */}
            <div className="lg:col-span-5">
              <FullLogo height={42} />
              <p
                className="font-display-italic mt-5 max-w-[420px]"
                style={{ color: "var(--nexxis-ink-muted)", fontSize: 18, lineHeight: 1.55 }}
              >
                {t.footer.desc}
              </p>
              <div className="mt-7 flex items-center gap-3">
                {SOCIALS.map(({ icon: Icon, label, href }) => (
                  <a
                    key={label}
                    href={href}
                    aria-label={label}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="nxx-soc inline-flex h-9 w-9 items-center justify-center rounded-full"
                  >
                    <Icon size={16} strokeWidth={1.6} />
                  </a>
                ))}
              </div>
            </div>

            {/* navigate */}
            <div className="lg:col-span-3">
              <div className="kicker mb-4 flex items-center gap-2">
                <span className="inline-block h-[2px] w-3.5" style={{ background: "#FF6857" }} />
                {t.footer.navigate}
              </div>
              <ul className="space-y-2.5 font-ui text-[13px]">
                {NAV_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="link-draw" style={{ color: "var(--nexxis-ink)" }}>
                      {t.nav[l.key]}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* direct */}
            <div className="lg:col-span-4">
              <div className="kicker mb-4 flex items-center gap-2">
                <span className="inline-block h-[2px] w-3.5" style={{ background: "#FF6857" }} />
                {t.footer.direct}
              </div>
              <a
                href="mailto:info@nexxishub.com"
                data-testid={FOOTER.emailLink}
                className="font-display block transition-opacity hover:opacity-80"
                style={{ fontSize: 24, color: "var(--nexxis-ink)", letterSpacing: "-0.01em" }}
              >
                info@nexxishub.com
              </a>
              <div className="mt-3 h-px" style={{ background: "#FF6857", width: 40 }} />
              <p className="font-ui mt-3 text-[13px]" style={{ color: "var(--nexxis-ink-muted)" }}>
                {t.footer.directNote}
              </p>
              <div className="mt-6 grid gap-3 font-ui text-[13px]" style={{ color: "var(--nexxis-ink-muted)" }}>
                <div className="flex items-center gap-2.5">
                  <MapPin size={15} strokeWidth={1.6} style={{ color: "#0D5F5E" }} />
                  <span>{t.footer.location}</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Clock size={15} strokeWidth={1.6} style={{ color: "#0D5F5E" }} />
                  <span>{t.footer.response}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* disclaimer bar */}
        <div
          className="mt-10 flex flex-col gap-3 text-[12px] font-ui sm:flex-row sm:items-center sm:justify-between"
          style={{ color: "var(--nexxis-ink-muted)" }}
        >
          <div>© {new Date().getFullYear()} NEXXIS — {t.footer.rights}</div>
          <div className="flex items-center gap-4">
            <span style={{ color: "var(--nexxis-ink)" }}>{t.footer.disc1}</span>
            <span className="coral-rule" aria-hidden="true" />
            <span>{t.footer.disc2}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
